// Language-wise voice audio resolver for browser-played cues (e.g. the campaign
// invite audio on the create screen).
//
// This branch plays the scan-flow voice cues natively (React Native), so it does
// NOT ship the in-browser scan speech engine. This module is intentionally
// small: it only fetches the per-language voice manifest
// (POST /res/get_voice_audio_files -> { status, files: { <cue>: <mp3 url> } })
// and resolves a single cue's URL, so web screens can play a localized clip with
// a bundled fallback.

import storage from '../Assets/js/Global';
import { urls } from '../Url';

const COMPANY_KEY = process.env.REACT_APP_COMPANY_ID || '';

// Per-language manifest cache: lang -> Promise<manifest | null>. Each language is
// fetched at most once for the lifetime of the page.
const manifestCache = {};

function fetchManifest(lang) {
  const target = String(lang || storage.get('selectedLanguage') || 'en').toLowerCase();
  if (manifestCache[target]) return manifestCache[target];
  manifestCache[target] = fetch(urls.getVoiceAudioFiles, {
    method: 'POST',
    credentials: 'omit',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ companyKey: COMPANY_KEY, lang: target }),
  })
    .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
    .then((data) => {
      if (!data || data.status !== true || !data.files) {
        throw new Error('malformed voice manifest');
      }
      return data;
    })
    .catch((err) => {
      console.warn(`[voiceAudio] manifest load failed for "${target}":`, err?.message || err);
      delete manifestCache[target]; // permit a retry later
      return null;
    });
  return manifestCache[target];
}

// Resolve a single cue's URL for a language (e.g. "invite"). Resolves to null if
// the cue/language is unavailable so callers can fall back to a bundled file.
export function getVoiceUrl(key, lang) {
  return fetchManifest(lang).then((data) => (data && data.files ? data.files[key] || null : null));
}

// Resolve the full cue -> url map for a language. Shares the same per-language
// cache as getVoiceUrl, so callers that need every clip (e.g. the native scan
// audio bridge) hit the endpoint at most once per language. Resolves to null on
// failure so callers keep whatever they already had.
export function getVoiceFiles(lang) {
  return fetchManifest(lang).then((data) => (data && data.files ? data.files : null));
}

// Warm the per-language manifest cache. Idempotent per language via
// manifestCache: safe to call on every language change; it hits the API only the
// first time a given language is requested. Call once after login / on language
// change so screens resolve their audio instantly from cache instead of paying
// the get_voice_audio_files round-trip on mount.
export function prefetchVoiceManifest(lang) {
  return fetchManifest(lang);
}

// ---------------------------------------------------------------------------
// Guideline-screen audio
// ---------------------------------------------------------------------------
// The WebView only lets an <audio> element play if that element was first
// played during a tap. So every guideline cue goes through ONE shared element:
// unlockGuidelineAudio() plays a silent clip on it inside the tap that opens the
// guidelines, and from then on the same element can switch src and play (e.g.
// when the modal finishes opening) without another tap. A fresh `new Audio()`
// per cue would be blocked every time it isn't started directly by a tap.

const GUIDELINE_CUES = [
  'hold-steady',
  'not-too-low',
  'not-too-high',
  'face-forward',
  'even-lighting',
  'remove-glasses',
  'final-reminder',
];

// 10ms of silence (8kHz, 8-bit mono).
const SILENT_WAV =
  'data:audio/wav;base64,UklGRnQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YVAAAACAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgA==';

const guidelineLang = (lang) =>
  String(lang || storage.get('selectedLanguage') || 'en').toLowerCase();

let guidelineEl = null;
let guidelineUnlocked = false;
// cue -> URL for guidelineUrlLang (populated by preloadGuidelineAudio).
let guidelineUrlMap = {};
let guidelineUrlLang = null;
let guidelinePreloadPromise = null;
let guidelinePreloadLang = null;
// Read at load so a reload respects the saved preference before any component mounts.
let guidelineMuted = storage.get('voiceAssist') === false;
// Bumped on every play/stop so a cue still waiting on the manifest is dropped if
// the user has moved to another slide or closed the modal by the time it arrives.
let guidelinePlayToken = 0;

function getGuidelineEl() {
  if (!guidelineEl) {
    guidelineEl = new Audio();
    guidelineEl.preload = 'auto';
    // These cues load from a cross-origin CDN (auth.healthy4life.ai) — without
    // this, a page with Cross-Origin-Embedder-Policy active defaults the
    // request to same-origin and Chrome blocks it
    // (ERR_BLOCKED_BY_RESPONSE.NotSameOriginAfterDefaultedToSameOriginByCoep).
    guidelineEl.crossOrigin = 'anonymous';
  }
  return guidelineEl;
}

// Must be called synchronously inside a tap handler, before anything async.
export function unlockGuidelineAudio() {
  if (guidelineUnlocked) return;
  guidelineUnlocked = true;
  const el = getGuidelineEl();
  try {
    el.src = SILENT_WAV;
    const p = el.play();
    if (p && typeof p.then === 'function') {
      p.then(
        () => console.log('[voiceAudio] unlockGuidelineAudio: audio unlocked'),
        (err) => {
          if (err?.name !== 'NotAllowedError') return; // AbortError = a real cue took over
          guidelineUnlocked = false; // wasn't inside a tap — let the next tap retry
          console.warn('[voiceAudio] unlockGuidelineAudio: blocked (not called from a tap)');
        },
      );
    }
  } catch (err) {
    guidelineUnlocked = false;
    console.warn('[voiceAudio] unlockGuidelineAudio: error', err);
  }
}

// Fetch the voice manifest and warm the HTTP cache for every guideline cue.
// Called on Dashboard mount; reuses the in-flight/finished load for the same language.
export function preloadGuidelineAudio(lang) {
  const target = guidelineLang(lang);
  if (guidelinePreloadPromise && guidelinePreloadLang === target) return guidelinePreloadPromise;
  console.log(`[voiceAudio] preloadGuidelineAudio: fetching manifest (${target})...`);
  guidelinePreloadLang = target;
  const promise = getVoiceFiles(target).then((files) => {
    if (!files) {
      console.warn(`[voiceAudio] preloadGuidelineAudio: manifest unavailable (${target})`);
      if (guidelinePreloadPromise === promise) guidelinePreloadPromise = null; // allow a retry
      return;
    }
    const next = {};
    GUIDELINE_CUES.forEach((cue) => {
      const url = files[cue];
      if (!url) return;
      next[cue] = url;
      if (url !== guidelineUrlMap[cue]) {
        fetch(url, { method: 'GET', credentials: 'omit' }).catch(() => {});
      }
    });
    if (guidelinePreloadLang !== target) return; // a newer language load superseded this one
    guidelineUrlMap = next;
    guidelineUrlLang = target;
    console.log(`[voiceAudio] preloadGuidelineAudio: all guideline audio loaded (${target})`, Object.keys(next));
  });
  guidelinePreloadPromise = promise;
  return promise;
}

export function setGuidelineVoiceMuted(muted) {
  guidelineMuted = !!muted;
  if (guidelineMuted) stopGuidelineAudio();
}

export function isGuidelineVoiceMuted() {
  return guidelineMuted;
}

function startGuidelineCue(cue, url) {
  const el = getGuidelineEl();
  try {
    el.pause();
    el.src = url;
    // iOS WKWebView doesn't reliably pick up a new `src` on a reused element
    // just from play() — without an explicit load() here, playback can stall
    // for several seconds (or never start) on iOS, even though Android's
    // Chromium-based WebView handles the same src swap instantly.
    el.load();
    const p = el.play();
    if (p && typeof p.then === 'function') {
      p.then(
        () => {
          guidelineUnlocked = true;
          console.log(`[voiceAudio] playGuidelineCue: playing "${cue}"`);
        },
        (err) => {
          if (err?.name === 'AbortError') return; // replaced by a newer cue or stopped
          console.warn(`[voiceAudio] playGuidelineCue: play failed for "${cue}"`, err?.name, err?.message);
        },
      );
    }
  } catch (err) {
    console.warn(`[voiceAudio] playGuidelineCue: error for "${cue}"`, err);
  }
}

export function playGuidelineCue(cue) {
  const token = ++guidelinePlayToken;
  const lang = guidelineLang();
  const url = guidelineUrlLang === lang ? guidelineUrlMap[cue] : null;
  console.log(`[voiceAudio] playGuidelineCue: cue="${cue}" muted=${guidelineMuted} urlReady=${!!url} unlocked=${guidelineUnlocked}`);
  if (guidelineMuted) return;
  if (url) {
    startGuidelineCue(cue, url);
    return;
  }
  // The cue will start after an await, outside any tap — unlock now in case this
  // call is running inside one.
  unlockGuidelineAudio();
  console.log(`[voiceAudio] playGuidelineCue: waiting for manifest for "${cue}"...`);
  preloadGuidelineAudio(lang).then(() => {
    if (token !== guidelinePlayToken || guidelineMuted) return;
    const readyUrl = guidelineUrlMap[cue];
    if (readyUrl) startGuidelineCue(cue, readyUrl);
    else console.warn(`[voiceAudio] playGuidelineCue: no URL for cue "${cue}"`);
  });
}

export function stopGuidelineAudio() {
  guidelinePlayToken++;
  if (guidelineEl) {
    try { guidelineEl.pause(); } catch {}
  }
}
